import { type DocNode } from '@atlaskit/adf-schema';
import { findFilepaths, isLinkInMatches } from '@atlaskit/editor-common/utils';
import { ADFEncoder } from '@atlaskit/renderer/utils';

import { AiMateMarkdownTransformer } from '../content-transformer/md-tokenizer/markdown-transformer';
import {
	ROVO_SKILL_COMBINED_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
	ROVO_SKILL_TAG_PATTERN,
} from '../skills';

export type Content = DocNode | string;

export const isAdfContent = (content: Content): content is DocNode => {
	/*
	This try-catch is a necessary guard rail against broken content to prevent higher level
	error boundaries from displaying the error and breaking the experience.
	*/
	try {
		return typeof content !== 'string' && 'type' in content && content.type === 'doc';
	} catch {
		return false;
	}
};

export const appendContent = (
	content: string | DocNode,
	suggestionContent: string,
): string | DocNode => {
	if (isAdfContent(content)) {
		// Ensure the content is an array of nodes
		const updatedContent = Array.isArray(content.content) ? content.content : [content.content];

		// Append the new paragraph node
		updatedContent.push({
			type: 'paragraph',
			content: [
				{
					type: 'text',
					text: suggestionContent,
				},
			],
		});

		// Return the updated DocNode
		return {
			...content,
			content: updatedContent,
		};
	}
	return `${content}\n\n${suggestionContent}`;
};

const pluginConfig = {
	linkify: {
		findFilepaths,
		isLinkInMatches,
	},
	smartLinks: {
		regexStorage: globalThis.localStorage,
	},
};

// Default schema is https://bitbucket.org/atlassian/adf-schema/src/main/packages/adf-schema/src/schema/default-schema.ts
export const adfEncoder = new ADFEncoder(
	(schema) => new AiMateMarkdownTransformer(schema, pluginConfig, false),
);

const streamingAdfEncoder = new ADFEncoder(
	(schema) => new AiMateMarkdownTransformer(schema, pluginConfig, true),
);

export const encodeAdf = (
	content: Content,
	streaming: boolean = false,
	isExperimentEnabled?: (experimentName: string, needsExposure?: boolean) => boolean,
	shouldShowAsInline?: boolean,
): DocNode => {
	if (isAdfContent(content)) {
		return content;
	}

	if (isExperimentEnabled) {
		return new ADFEncoder(
			(schema) =>
				new AiMateMarkdownTransformer(
					schema,
					pluginConfig,
					streaming,
					isExperimentEnabled,
					shouldShowAsInline,
				),
		).encode(content);
	}

	return streaming ? streamingAdfEncoder.encode(content) : adfEncoder.encode(content);
};

export const encodeSkillPromptToAdf = (content: string): { adf: DocNode; promptLength: number } => {
	const contentNodes: Array<
		| { type: 'text'; text: string }
		| {
				type: 'inlineExtension';
				attrs: { extensionKey: string; extensionType: string; parameters: { slug: string } };
		  }
	> = [];
	let lastIndex = 0;
	let match;
	let promptLength = 0;

	// Find all skill tag matches
	const matches: Array<{ index: number; skillSlug: string; length: number }> = [];
	while ((match = ROVO_SKILL_TAG_PATTERN.exec(content)) !== null) {
		matches.push({
			index: match.index,
			skillSlug: match[1], // The skill name without the slash
			length: match[0].length, // Length of "/skill-name"
		});
	}

	// Process the string segment by segment
	for (let i = 0; i < matches.length; i++) {
		const currentMatch = matches[i];
		const matchStart = currentMatch.index;
		const matchEnd = matchStart + currentMatch.length;

		// Add text before the skill tag (if any)
		if (matchStart > lastIndex) {
			const textBefore = content.substring(lastIndex, matchStart);
			if (textBefore) {
				contentNodes.push({
					type: 'text',
					text: textBefore,
				});
				promptLength += textBefore.length;
			}
		}

		// Add the skill tag as inlineExtension
		contentNodes.push({
			type: 'inlineExtension',
			attrs: {
				extensionKey: ROVO_SKILL_COMBINED_EXTENSION_KEY,
				extensionType: ROVO_SKILL_EXTENSION_TYPE,
				parameters: {
					slug: currentMatch.skillSlug,
				},
			},
		});
		promptLength += 1;

		lastIndex = matchEnd;
	}

	// Add remaining text after the last match (if any)
	if (lastIndex < content.length) {
		const textAfter = content.substring(lastIndex);
		if (textAfter) {
			contentNodes.push({
				type: 'text',
				text: textAfter,
			});
			promptLength += textAfter.length;
		}
	}

	// If no matches were found, return the entire string as text
	if (contentNodes.length === 0) {
		contentNodes.push({
			type: 'text',
			text: content,
		});
		promptLength += content.length;
	}

	return {
		adf: {
			type: 'doc',
			version: 1,
			content: [
				{
					type: 'paragraph',
					content: contentNodes,
				},
			],
		},
		promptLength,
	};
};
